import { CensusVariables } from '@/constants/territories/census-variables';
import { POLYGON_COLORS } from '@/constants/territories/colors';
import { US_STATES } from '@/constants/territories/states';
import { AreaInfo } from '@/types/territories/area.info.type';
import { TerritorySettings } from '@/types/territories/territory.settings.type';
import { TerritoryStatus } from '@/types/territories/territory.status.type';

export interface AggregatedStats {
  state: string;
  zctaCodes: string[];
  totalPopulation: number;
  totalMedianAge: number;
  totalBirthRate: number;
  totalMedianHomeValue: number;
  totalMedianHouseholdIncome: number;
  totalPopOver40: number;
}

export const calculateAggregatedStats = (
  areaInfos: AreaInfo[]
): AggregatedStats => {
  const initialStats: AggregatedStats = {
    state: areaInfos[0].state,
    zctaCodes: areaInfos.map((area) => area.zcta_code),
    totalPopulation: 0,
    totalMedianAge: 0,
    totalBirthRate: 0,
    totalMedianHomeValue: 0,
    totalMedianHouseholdIncome: 0,
    totalPopOver40: 0,
  };

  const resultStats = areaInfos.reduce((stats, area) => {
    const population = area.population;
    stats.totalPopulation += population;
    stats.totalMedianAge += area.median_age * population;
    stats.totalBirthRate += area.birth_rate * population;
    stats.totalMedianHomeValue += area.median_home_value * population;
    stats.totalMedianHouseholdIncome +=
      area.median_household_income * population;
    stats.totalPopOver40 += area.pop_over_40;
    return stats;
  }, initialStats);

  return {
    state: resultStats.state,
    zctaCodes: resultStats.zctaCodes,
    totalPopulation: resultStats.totalPopulation,
    totalMedianAge: resultStats.totalMedianAge / resultStats.totalPopulation,
    totalBirthRate: resultStats.totalBirthRate / resultStats.totalPopulation,
    totalMedianHomeValue:
      resultStats.totalMedianHomeValue / resultStats.totalPopulation,
    totalMedianHouseholdIncome:
      resultStats.totalMedianHouseholdIncome / resultStats.totalPopulation,
    totalPopOver40: resultStats.totalPopOver40,
  };
};

export const getTerritoryColor = (
  status: TerritoryStatus,
  territorySettings: TerritorySettings | undefined
): string => {
  // RESELL has no editable override field on TerritorySettings.colors (no
  // color-settings form in this dashboard) — always use the default purple.
  if (status === 'RESELL') {
    return POLYGON_COLORS.RESELL;
  }

  const territoryColor = territorySettings?.colors
    ? territorySettings?.colors[
        status.toLowerCase() as Lowercase<Exclude<TerritoryStatus, 'RESELL'>>
      ]
    : POLYGON_COLORS[status];

  return territoryColor;
};

/**
 * External-facing status masking.
 *
 * RESELL is internal-only (franchisor + consultant). Any output that can
 * leave those roles — PDF/report exports, ZEE, public pages — must never
 * surface "Territory Resell"; it reads as an available territory, in both
 * status text and color.
 */
/**
 * A resell must read IDENTICALLY to a genuine available territory in external
 * output (same masked status text), so an external reader can never distinguish
 * the two — neither per-territory nor in aggregated counts.
 */
export const getExternalTerritoryStatusText = (
  status: TerritoryStatus
): string => (status === 'RESELL' ? 'AVAILABLE' : status);

export const getExternalTerritoryColor = (
  status: TerritoryStatus,
  territorySettings: TerritorySettings | undefined
): string =>
  getTerritoryColor(
    status === 'RESELL' ? 'AVAILABLE' : status,
    territorySettings
  );

export const getFirstMatchingStateCode = (value: string): string => {
  if (!value.trim()) return '';

  const lowerSearch = value.toLowerCase();

  const match = Object.entries(US_STATES).find(([, fullName]) =>
    fullName.toLowerCase().includes(lowerSearch)
  );

  return match?.[0] || value;
};

export const isDecimal = (code: string): boolean => {
  return Object.values(CensusVariables).some(
    (variable) =>
      variable.code === code &&
      !variable.label.toLowerCase().includes('income') &&
      !variable.label.toLowerCase().includes('value')
  );
};
